import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PaymentRecordType } from '../../../../enums/payables/payable-action.enum';
import { Transaction } from '../../../../models/entities/transactions/transaction';
import { Payable } from '../../../../models/entities/payable';
import { CommonModule } from '@angular/common';
import { BankAccountSelectorComponent } from '../../shared/bank-account-selector/bank-account-selector.component';
import { LegfiDatePipe } from 'app/pipes/format/legfi-date.pipe';
import { LegfiCurrencyPipe } from 'app/pipes/format/legfi-currency.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

interface RecordTypeItem {
    value: PaymentRecordType;
    label: string;
    disabled?: boolean;
}

interface TransactionItem {
    value: number;
    label: string;
}

@UntilDestroy()
@Component({
    selector: 'app-mark-as-paid-form',
    templateUrl: './mark-as-paid-form.component.html',
    styleUrls: ['./mark-as-paid-form.component.scss'],
    standalone: true,
    imports: [CommonModule,
        ReactiveFormsModule,
        BankAccountSelectorComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MarkAsPaidFormComponent implements OnInit {
    @Input() localPayable: Payable;
    @Input() potentialTransactionMatches: Transaction[] = [];

    markAsPaidFormGroup: UntypedFormGroup;

    readonly PaymentRecordType = PaymentRecordType;
    readonly CREATE_PAYMENT_FIELDS = ['bankAccountForTransaction', 'paymentDate', 'paymentMemo'] as const;
    readonly REQUIRED_FIELDS_FOR_CREATE = ['bankAccountForTransaction', 'paymentDate'] as const;

    recordTypeItems: RecordTypeItem[] = [];
    transactionItems: TransactionItem[] = [];

    constructor(
            private _formBuilder: UntypedFormBuilder,
            private _legfiDatePipe: LegfiDatePipe,
            private _legfiCurrencyPipe: LegfiCurrencyPipe,
    ) { }

    ngOnInit(): void {
        this.markAsPaidFormGroup = this.initMarkAsPaidForm();
        this.setupPaymentRecordTypeValidation();
        this.handleTransactionMatchingState(this.potentialTransactionMatches);
    }

    handleTransactionMatchingState(matches: Transaction[]): void {
        const paymentRecordTypeControl = this.markAsPaidFormGroup.get('paymentRecordType');
        const matchControl = this.markAsPaidFormGroup.get('existingTransactionToMatch');

        this.recordTypeItems = [
            {
                value: PaymentRecordType.MATCH,
                label: 'Match with an existing transaction',
                disabled: matches.length === 0,
            },
            {
                value: PaymentRecordType.CREATE,
                label: 'Create a new payment record',
            },
        ];
        this.transactionItems = matches.map(transaction => ({
            value: transaction.id,
            label: `${this._legfiDatePipe.transform(transaction.transactionDate, 'MM/DD/YYYY')} • ${transaction.description} • ${this._legfiCurrencyPipe.transform(transaction.amount, 'USD', 'symbol', '.2')}`,
        }));

        if (matches.length > 0) {
            matchControl.enable();
            paymentRecordTypeControl.setValue(PaymentRecordType.MATCH);
        } else {
            matchControl.disable();
            matchControl.setValue(null);
            paymentRecordTypeControl.setValue(PaymentRecordType.CREATE);
        }
    }

    isValid(): boolean {
        this.markAsPaidFormGroup.markAllAsTouched();
        return this.markAsPaidFormGroup.valid;
    }

    getPaymentDate(): string | null {
        return this.markAsPaidFormGroup.get('paymentDate').value;
    }

    getPaymentBankAccountId(): number | null {
        return this.markAsPaidFormGroup.get('bankAccountForTransaction').value?.id || null;
    }

    getFormGroup(): UntypedFormGroup {
        return this.markAsPaidFormGroup;
    }

    markAsUntouched(): void {
        this.markAsPaidFormGroup.markAsUntouched();
    }

    private initMarkAsPaidForm(): UntypedFormGroup {
        return this._formBuilder.group({
            paymentRecordType: this._formBuilder.control(PaymentRecordType.CREATE, {
                nonNullable: true
            }),
            existingTransactionToMatch: this._formBuilder.control({
                value: null,
                disabled: true
            }),
            bankAccountForTransaction: this._formBuilder.control(null),
            paymentDate: this._formBuilder.control(null),
            paymentMemo: this._formBuilder.control(null)
        });
    }

    private setupPaymentRecordTypeValidation(): void {
        this.markAsPaidFormGroup.get('paymentRecordType').valueChanges.pipe(untilDestroyed(this)).subscribe(recordType => {
            if (recordType === PaymentRecordType.CREATE) {
                this.setupCreatePaymentValidation();
            } else if (recordType === PaymentRecordType.MATCH) {
                this.setupMatchPaymentValidation();
            }
            this.markAsPaidFormGroup.updateValueAndValidity();
        });
    }

    private setupCreatePaymentValidation(): void {
        this.REQUIRED_FIELDS_FOR_CREATE.forEach(field => {
            const control = this.markAsPaidFormGroup.get(field);
            control.setValidators([Validators.required]);
            control.updateValueAndValidity();
        });

        const matchControl = this.markAsPaidFormGroup.get('existingTransactionToMatch');
        matchControl.clearValidators();
        matchControl.disable();
        matchControl.setValue(null);
    }

    private setupMatchPaymentValidation(): void {
        const matchControl = this.markAsPaidFormGroup.get('existingTransactionToMatch');
        matchControl.setValidators([Validators.required]);
        matchControl.enable();
        matchControl.updateValueAndValidity();

        this.CREATE_PAYMENT_FIELDS.forEach(field => {
            const control = this.markAsPaidFormGroup.get(field);
            control.clearValidators();
            control.setValue(null);
            control.updateValueAndValidity();
        });
    }
}
